"use client";

import { Eye, EyeOff } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import { FieldValues, Path, PathValue } from "react-hook-form";

import { cn } from "@/lib/utils";
import { InputProps } from "@/types/type";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  FormItem,
  FormLabel,
  FormField,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectItem,
  SelectValue,
  SelectContent,
  SelectTrigger,
} from "@/components/ui/select";
import { Textarea } from "./ui/textarea";
import { P } from "./typo";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";

export const PrimaryInput = <T extends FieldValues>({
  name,
  form,
  type,
  label,
  value,
  options,
  onSelect,
  required,
  disabled,
  className,
  isLoading,
  orientation,
  selectValue,
  placeholder,
  doubleClickFunc,
}: InputProps<T>) => {
  //   const [internalValue, setInternalValue] = useState<string | undefined>(
  //     selectValue
  //   );
  const [hidden, setHidden] = useState<boolean>(true);
  const EYE = hidden ? EyeOff : Eye;
  //   useEffect(() => {
  //     if (reset) {
  //       setInternalValue(undefined);
  //       form?.setValue(name, "" as PathValue<T, Path<T>>);
  //     }
  //   }, [reset, name, form]);

  return (
    <FormField
      control={form?.control}
      name={name}
      render={({ field }) => (
        <FormItem
          className={cn(
            orientation === "horizontal" &&
              "flex flex-row items-center gap-2 space-y-0",
          )}
          // onDoubleClick={() => console.log("gaguh")}
        >
          <FormLabel className=" font-medium text-[14x] capitalize">
            {label}
            {required && <span className="text-red-400 mx-[2px]">*</span>}
          </FormLabel>
          {type === "select" ? (
            <div
              onDoubleClickCapture={doubleClickFunc}
              className={cn(disabled ? "cursor-not-allowed" : "")}
            >
              <Select
                // value={internalValue}
                value={form?.getValues(name)}
                onValueChange={(value: PathValue<T, Path<T>>) => {
                  field.onChange(value);
                  // @ts-ignore
                  form?.setValue(name, value);
                  if (onSelect) {
                    // @ts-ignore
                    onSelect(value);
                  }
                }}
              >
                <FormControl>
                  <SelectTrigger
                    className={cn(
                      "text-[14px] font-normal min-w-[120px] disabled:pointer-events-none bg-input",
                      form?.getValues(name)
                        ? "text-foreground"
                        : "text-muted-foreground",
                      className,
                    )}
                    disabled={disabled}
                  >
                    <SelectValue
                      placeholder={placeholder}
                      className={cn(
                        "placeholder:text-muted-foreground placeholder:opacity-20",
                        // form?.getValues(name)
                        //   ? "text-foreground"
                        //   : "text-muted-foreground"
                      )}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {options?.length ? (
                    options.map((item, index) => (
                      <SelectItem
                        key={index}
                        value={item.value}
                        className="text-left cursor-pointer"
                        disabled={item.disabled}
                      >
                        {item.title}
                      </SelectItem>
                    ))
                  ) : isLoading ? (
                    <div className="space-y-1.5 p-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} className="h-7 w-full" />
                      ))}
                    </div>
                  ) : (
                    <P className="p-2">No items</P>
                  )}
                </SelectContent>
              </Select>
            </div>
          ) : type === "file" ? (
            <FormControl>
              <div className="relative flex items-center w-auto">
                <Input
                  onChange={(e) => {
                    if (e.target.files) {
                      form?.setValue(field.name, e.target.files[0] as any);
                    }
                  }}
                  placeholder={placeholder}
                  readOnly={form?.formState.isSubmitting || disabled}
                  className={cn("w-full focus:border-border ", className)}
                  type={
                    type === "password" ? (hidden ? "password" : "text") : type
                  }
                  onDoubleClick={doubleClickFunc}
                />
                {type === "password" && form?.watch(name) && (
                  <EYE
                    className="absolute mr-2 text-muted-foreground cursor-pointer hover:text-teal-800 dark:hover:text-teal-400 right-3"
                    size={18}
                    onClick={() => setHidden((prev) => !prev)}
                  />
                )}
              </div>
            </FormControl>
          ) : type === "radio" ? (
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="flex flex-col space-y-1"
              >
                {options &&
                  options?.map((item, index) => (
                    <FormItem
                      className="flex items-center space-x-3 space-y-0"
                      key={index}
                    >
                      <FormControl>
                        <RadioGroupItem value={item.value} />
                      </FormControl>
                      <FormLabel className="font-normal">
                        {item.title}
                      </FormLabel>
                    </FormItem>
                  ))}
              </RadioGroup>
            </FormControl>
          ) : (
            <FormControl>
              <div className="relative flex items-center w-auto ">
                <Input
                  {...field}
                  value={value ? value : form?.getValues(name)}
                  placeholder={placeholder}
                  onDoubleClickCapture={doubleClickFunc}
                  className={cn("focus:border-accent", className)}
                  readOnly={form?.formState.isSubmitting || disabled}
                  type={
                    type === "password" ? (hidden ? "password" : "text") : type
                  }
                  autoComplete="false"
                />
                {type === "password" && form?.watch(name) && (
                  <EYE
                    className="absolute mr-2 text-muted-foreground cursor-pointer hover:text-primary right-3"
                    size={18}
                    onClick={() => setHidden((prev) => !prev)}
                  />
                )}
              </div>
            </FormControl>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const SecondaryInput = <T extends FieldValues>({
  name,
  form,
  Icon,
  type,
  label,
  disabled,
  className,
}: InputProps<T>) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focus, setFocus] = useState<boolean>(false);
  const [hidden, setHidden] = useState<boolean>(true);
  const [isAutocompleted, setIsAutocompleted] = useState(false);

  const watchInput = form?.watch(name);
  const excludedTypes = ["date", "file"];

  const EYE = hidden ? EyeOff : Eye;

  const handleFocus = () => setFocus(true);
  const handleBlur = () => !watchInput && setFocus(false);

  const showLabel =
    label && !focus && !excludedTypes.includes(type!) && !isAutocompleted;

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const checkAutocomplete = () => {
      if (input.value) {
        // setIsAutocompleted(true);
        setFocus(true);
      }
    };

    // Modify the input slightly after page load
    const randomId = `input-${Math.random().toString(36).substr(2, 9)}`;
    setTimeout(() => {
      input.name = randomId;
      input.setAttribute("autocomplete", "new-password");
    }, 500);

    input.addEventListener("animationstart", checkAutocomplete);
    input.addEventListener("input", checkAutocomplete);

    return () => {
      input.removeEventListener("animationstart", checkAutocomplete);
      input.removeEventListener("input", checkAutocomplete);
    };
  }, []);

  return (
    <FormField
      control={form?.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <div
              className={cn(
                "relative flex items-center border rounded-lg p-2 w-full cursor-text min-h-[40px]",
                focus ? "border-border" : "border-border-secondary",
                className,
              )}
            >
              {Icon && (
                <Icon.name
                  className={cn(
                    "mx-2 text-primary",
                    focus ? "opacity-85" : "opacity-40",
                  )}
                  size={Icon.size}
                />
              )}
              {showLabel && (
                <Label
                  htmlFor={label}
                  className={cn(
                    "capitalize cursor-text text-accent whitespace-nowrap pointer-events-none",
                    Icon ? "ml-4" : "ml-2",
                  )}
                >
                  {label}
                </Label>
              )}
              <Input
                {...field}
                ref={inputRef}
                type={
                  type === "password" ? (hidden ? "password" : "text") : type
                }
                onFocus={handleFocus}
                onBlur={handleBlur}
                className={cn(
                  "absolute w-full h-full border-0 bg-transparent",
                  Icon ? "pl-14" : "pl-1",
                  "autofill-override",
                )}
                disabled={form?.formState.isSubmitting || disabled}
                placeholder=""
                autoComplete="new-password"
              />
              {type === "password" && watchInput && (
                <EYE
                  className="absolute mr-2 text-muted-foreground cursor-pointer hover:text-primary right-3"
                  size={20}
                  onClick={() => setHidden((prev) => !prev)}
                />
              )}
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const PrimaryTextArea = <T extends FieldValues>({
  form,
  name,
  label,
  placeholder,
  required,
  disabled,
  className,
}: InputProps<T>) => {
  return (
    <FormField
      control={form?.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label}
            {required && <span className="text-red-400 mx-[2px]">*</span>}
          </FormLabel>

          <FormControl>
            <div className="relative flex items-center w-auto">
              <Textarea
                {...field}
                placeholder={placeholder}
                disabled={form?.formState.isSubmitting || disabled}
                className={cn("focus:border-border", className)}
              />
            </div>
          </FormControl>

          <FormMessage />
        </FormItem>
      )}
    />
  );
};
